import { useRef, useState, memo, useCallback, useEffect } from "react";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import ImageUpload from "../image-upload/ImageUpload";
import { ameenInstructions } from "../../Instructions";
import { vectorFAQService } from "../../services/vectorFAQService";
import {
  AltairComponentProps,
  ClaimPolicyData,
  CompanyWithPackages,
  ContractedWorkshop,
  CustomerNationalIdData,
  FlowStep,
  NonContractedWorkshopFormData,
  PolicyPackage,
  VehicleSide,
  VehicleSurveyImage,
} from "../../interfaces/altair";
import {
  useAltairChartEffect,
  useGreetingEffect,
  useModelConfigEffect,
  useToolCallEffect,
} from "./altairEffects";
import { createHandleSelectPackage } from "./handleSelectPackage";
import { createHandleImagesUploaded } from "./handleImagesUploaded";
import { compressBase64Image } from "../../utils/imageCompression";
import {
  policyInquiry,
  chooseContractedWorkshop,
  chooseNonContractedWorkshop,
} from "../../services/claimService";
import { ExtractedDataConfirmationModal, ExtractedDataField } from "../extracted-data-confirmation";
import { PolicyNumberModal } from "../policy-number-modal";

// Extended props to include camera control callbacks from parent
export interface AltairWithCameraProps extends AltairComponentProps {
  onRequestStartCamera?: () => Promise<boolean>;
  onRequestCaptureFrame?: () => Promise<string | null>;
  onRequestStopCamera?: () => void;
}

function AltairComponent({
  permissions,
  userData,
  voiceGender = "male",
  onRequestStartCamera,
  onRequestCaptureFrame,
  onRequestStopCamera,
}: AltairWithCameraProps) {
  // Altair chart state
  const [jsonString, setJSONString] = useState<string>("");

  // Policy flow state
  const [flowStep, setFlowStep] = useState<FlowStep>("idle");
  const [companies, setCompanies] = useState<CompanyWithPackages[]>([]);
  const [selectedPackageCode, setSelectedPackageCode] = useState<string>("");

  const [customerNationalIdData, setCustomerNationalIdData] =
    useState<CustomerNationalIdData>({
      national_id_number: "",
      profession: "",
      gender: "",
      religion: "",
      marital_status: "",
      expiration_date: "",
      issue_batch_date: "",
      full_name: "",
      address: "",
      birth_date: "",
      card_serial_number: "",
      national_id_front_image_base64: "",
      national_id_back_image_base64: "",
    });

  // Vehicle Survey state
  const [surveyImages, setSurveyImages] = useState<VehicleSurveyImage[]>([]);

  // Claim Request state
  const [claimPolicyData, setClaimPolicyData] = useState<ClaimPolicyData | null>(null);
  const [contractedWorkshops, setContractedWorkshops] = useState<ContractedWorkshop[]>([]);
  const [claimSurveyImages, setClaimSurveyImages] = useState<VehicleSurveyImage[]>([]);
  const [policyNumberInput, setPolicyNumberInput] = useState<string>("");
  const [nonContractedForm, setNonContractedForm] = useState<NonContractedWorkshopFormData>({
    city: "",
    area: "",
    workshopAddress: "",
    workshopName: "",
    contactPersonName: "",
    mobile: "",
    phoneNumber: "",
    hotline: "",
  });

  const [hasGreeted, setHasGreeted] = useState<boolean>(false);

  // Modal states
  const [extractedDataModal, setExtractedDataModal] = useState<{
    isOpen: boolean;
    title: string;
    data: ExtractedDataField[];
    documentType: string;
    onConfirm: () => void;
    onSaveAndConfirm?: () => void;
  }>({
    isOpen: false,
    title: "",
    data: [],
    documentType: "",
    onConfirm: () => {},
    onSaveAndConfirm: undefined,
  });

  const [policyNumberModal, setPolicyNumberModal] = useState<{
    isOpen: boolean;
    policyNumber: string;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    policyNumber: "",
    title: "رقم الوثيقة",
    message: "تم إصدار الوثيقة بنجاح. يرجى حفظ رقم الوثيقة:",
    onConfirm: () => {},
  });

  const TEST_MODE = false;

  const { client, setConfig, setModel, connected } = useLiveAPIContext();
  const embedRef = useRef<HTMLDivElement>(null);

  // ═══════════════════════════════════════════════════════════
  // VEHICLE SURVEY CAMERA CALLBACKS
  // ═══════════════════════════════════════════════════════════

  const handleStartCameraStream = useCallback(async (): Promise<boolean> => {
    console.log("📹 [Altair] Starting camera stream for survey...");
    if (onRequestStartCamera) {
      const success = await onRequestStartCamera();
      if (success) {
        // Clear any previous survey images when starting a new survey
        // Determine if we're in ClaimFlow or PolicyFlow
        // Note: flowStep might not be updated yet when this is called, so we check both
        const isClaimFlow = flowStep === "claim_vehicle_survey" || flowStep?.startsWith("claim_") || false;
        if (isClaimFlow) {
          setClaimSurveyImages([]);
          // Also clear from session storage
          try {
            const existingData = sessionStorage.getItem("saaed_user_data");
            const userData = existingData ? JSON.parse(existingData) : {};
            userData.claimSurveyImages = [];
            sessionStorage.setItem("saaed_user_data", JSON.stringify(userData));
          } catch (error) {
            console.error("❌ [Altair] Failed to clear claim survey images from session storage:", error);
          }
        } else {
          setSurveyImages([]);
          // Also clear from session storage
          try {
            const existingData = sessionStorage.getItem("saaed_user_data");
            const userData = existingData ? JSON.parse(existingData) : {};
            userData.surveyImages = [];
            sessionStorage.setItem("saaed_user_data", JSON.stringify(userData));
          } catch (error) {
            console.error("❌ [Altair] Failed to clear policy survey images from session storage:", error);
          }
        }
      }
      return success;
    }
    console.warn("⚠️ [Altair] onRequestStartCamera not provided");
    return false;
  }, [onRequestStartCamera, flowStep]);

  const handleTakeVehiclePicture = useCallback(async (side: VehicleSide, currentFlowStep?: FlowStep): Promise<string | null> => {
    console.log(`📸 [Altair] Taking picture for side: ${side}`);
    if (onRequestCaptureFrame) {
      const imageData = await onRequestCaptureFrame();
      if (imageData) {
        // Compress the image automatically
        let compressedImageData = imageData;
        try {
          console.log(`🔄 [Altair] Compressing image for ${side}...`);
          compressedImageData = await compressBase64Image(imageData, {
            maxSizeMB: 2,
            maxWidthOrHeight: 1920,
            quality: 0.85,
          });
          console.log(`✅ [Altair] Image compressed for ${side}`);
        } catch (error) {
          console.error(`❌ [Altair] Error compressing image for ${side}, using original:`, error);
        }
        
        const newImage: VehicleSurveyImage = {
          side,
          data: compressedImageData,
          capturedAt: new Date().toISOString(),
        };
        
        // Determine if we're in ClaimFlow or PolicyFlow based on flowStep
        // Check both the passed flowStep parameter and the current flowStep state
        // Also check if we already have claim survey images as a fallback
        const isClaimFlow = (currentFlowStep || flowStep)?.startsWith("claim_") || 
                           flowStep === "claim_vehicle_survey" ||
                           (claimSurveyImages.length > 0 && surveyImages.length === 0);
        
        if (isClaimFlow) {
          // Save to claim survey images for ClaimFlow
          setClaimSurveyImages(prev => {
            const filtered = prev.filter(img => img.side !== side);
            const updated = [...filtered, newImage];
            
            // Also save to session storage
            try {
              const existingData = sessionStorage.getItem("saaed_user_data");
              const userData = existingData ? JSON.parse(existingData) : {};
              const existingImages = userData.claimSurveyImages || [];
              const filteredImages = existingImages.filter((img: VehicleSurveyImage) => img.side !== side);
              userData.claimSurveyImages = [...filteredImages, newImage];
              sessionStorage.setItem("saaed_user_data", JSON.stringify(userData));
              console.log(`✅ [Altair] Claim survey image for ${side} saved to state and session storage`);
            } catch (error) {
              console.error("❌ [Altair] Failed to save claim survey image to session storage:", error);
            }
            
            return updated;
          });
        } else {
          // Save to policy survey images for PolicyFlow
          setSurveyImages(prev => {
            const filtered = prev.filter(img => img.side !== side);
            const updated = [...filtered, newImage];
            
            // Also save to session storage
            try {
              const existingData = sessionStorage.getItem("saaed_user_data");
              const userData = existingData ? JSON.parse(existingData) : {};
              const existingImages = userData.surveyImages || [];
              const filteredImages = existingImages.filter((img: VehicleSurveyImage) => img.side !== side);
              userData.surveyImages = [...filteredImages, newImage];
              sessionStorage.setItem("saaed_user_data", JSON.stringify(userData));
              console.log(`✅ [Altair] Policy survey image for ${side} saved to state and session storage`);
            } catch (error) {
              console.error("❌ [Altair] Failed to save policy survey image to session storage:", error);
            }
            
            return updated;
          });
        }
        
        return compressedImageData;
      }
    }
    console.warn("⚠️ [Altair] onRequestCaptureFrame not provided or failed");
    return null;
  }, [onRequestCaptureFrame, flowStep, claimSurveyImages, surveyImages]);

  const handleCloseCameraStream = useCallback(() => {
    console.log("📹 [Altair] Closing camera stream...");
    if (onRequestStopCamera) {
      onRequestStopCamera();
    }
  }, [onRequestStopCamera]);

  const getSurveyImages = useCallback((): VehicleSurveyImage[] => {
    return surveyImages;
  }, [surveyImages]);

  // ═══════════════════════════════════════════════════════════
  // CLAIM REQUEST CALLBACKS
  // ═══════════════════════════════════════════════════════════

  const getClaimSurveyImages = useCallback((): VehicleSurveyImage[] => {
    console.log("📸 [Altair] getClaimSurveyImages called, current claimSurveyImages count:", claimSurveyImages.length);
    
    // Try to get from state first
    if (claimSurveyImages && claimSurveyImages.length > 0) {
      console.log("✅ [Altair] Returning claim survey images from state:", claimSurveyImages.length);
      return claimSurveyImages;
    }
    
    // Fallback: try to get from session storage
    try {
      const existingData = sessionStorage.getItem("saaed_user_data");
      if (existingData) {
        const userData = JSON.parse(existingData);
        if (userData.claimSurveyImages && Array.isArray(userData.claimSurveyImages) && userData.claimSurveyImages.length > 0) {
          console.log("✅ [Altair] Returning claim survey images from session storage:", userData.claimSurveyImages.length);
          return userData.claimSurveyImages;
        }
      }
    } catch (error) {
      console.error("❌ [Altair] Error reading claim survey images from session storage:", error);
    }
    
    console.warn("⚠️ [Altair] No claim survey images found in state or session storage");
    return [];
  }, [claimSurveyImages]);

  // Initialize vector FAQ service when component mounts
  useEffect(() => {
    vectorFAQService.initialize().catch(error => {
      console.error('❌ [Amin] Failed to initialize vector FAQ service:', error);
    });
  }, []);

  useGreetingEffect({ connected, client, hasGreeted, setHasGreeted });
  useModelConfigEffect({ setConfig, setModel, voiceGender, ameenInstructions });
  // Callback to show policy number modal
  const handleShowPolicyNumber = useCallback((
    policyNumber: string,
    title: string,
    message: string,
    onConfirm: () => void
  ) => {
    setPolicyNumberModal({
      isOpen: true,
      policyNumber,
      title,
      message,
      onConfirm: () => {
        setPolicyNumberModal(prev => ({ ...prev, isOpen: false }));
        onConfirm();
      },
    });
  }, []);

  useToolCallEffect({
    client,
    setJSONString,
    setCompanies,
    setFlowStep,
    setCustomerNationalIdData,
    onStartCameraStream: handleStartCameraStream,
    onTakeVehiclePicture: handleTakeVehiclePicture,
    onCloseCameraStream: handleCloseCameraStream,
    getSurveyImages,
    // Claim Request callbacks
    setClaimPolicyData,
    setContractedWorkshops,
    getClaimSurveyImages,
    onShowPolicyNumber: handleShowPolicyNumber,
  });
  useAltairChartEffect({ embedRef, jsonString });

  const handleSelectPackage = createHandleSelectPackage({
    setSelectedPackageCode,
    setFlowStep,
    client,
  });

  // Callback to show extracted data confirmation modal
  const handleShowExtractedDataConfirmation = useCallback((
    data: ExtractedDataField[],
    title: string,
    documentType: string,
    onConfirm: (editedData?: ExtractedDataField[]) => void,
    onSaveAndConfirm?: (editedData?: ExtractedDataField[]) => void
  ) => {
    setExtractedDataModal({
      isOpen: true,
      title,
      data,
      documentType,
      onConfirm: (editedData?: ExtractedDataField[]) => {
        setExtractedDataModal(prev => ({ ...prev, isOpen: false }));
        onConfirm(editedData);
      },
      onSaveAndConfirm: onSaveAndConfirm ? (editedData?: ExtractedDataField[]) => {
        setExtractedDataModal(prev => ({ ...prev, isOpen: false }));
        onSaveAndConfirm(editedData);
      } : undefined,
    });
  }, []);

  const handleImagesUploaded = createHandleImagesUploaded({
    client,
    flowStep,
    setCustomerNationalIdData,
    setFlowStep,
    onShowExtractedDataConfirmation: handleShowExtractedDataConfirmation,
  });

  const selectedPackage = (() => {
    for (const company of companies) {
      const pkg = company.companyPackages.find(
        (pkg: PolicyPackage) => pkg.packageCode === selectedPackageCode
      );
      if (pkg) return pkg;
    }
    return null;
  })();

  // Save the package price to sessionStorage when confirmation UI is displayed
  // This ensures the price shown matches what will be sent to GenerateFawryPayLink
  useEffect(() => {
    if (flowStep === "policy_confirmation" && selectedPackage) {
      try {
        sessionStorage.setItem("selected_package_price", selectedPackage.totalpremium.toString());
        console.log(`✅ [Altair] Saved policy confirmation price ${selectedPackage.totalpremium} to session storage (matches confirmation UI display)`);
      } catch (error) {
        console.error("❌ [Altair] Failed to save policy confirmation price to session storage:", error);
      }
    }
  }, [flowStep, selectedPackage]);

  const handleConfirmPolicyClick = useCallback(() => {
    if (!client) {
      console.warn("⚠️ [Altair] Client not available for confirm click");
      return;
    }

    console.log("✅ [Ameen] User clicked policy confirmation button");

    // Hide confirmation view immediately
    setFlowStep("idle");

    // Notify Ameen via system_ui so he can call confirm_policy_request tool
    client.send(
      [
        {
          text: "[system_ui] user_clicked_policy_confirm",
        },
      ],
      false
    );
    
    // Send next instruction
    setTimeout(() => {
      client.send(
        [
          {
            text: "[SYSTEM INSTRUCTION] Policy confirmed. Now call confirm_policy_request to generate and display the Fawry payment link.",
          },
        ],
        true
      );
    }, 300);
  }, [client, setFlowStep]);

  // ═══════════════════════════════════════════════════════════
  // CLAIM REQUEST EVENT HANDLERS
  // ═══════════════════════════════════════════════════════════

  const handlePolicyInquirySubmit = useCallback(async () => {
    if (!client || !policyNumberInput.trim()) {
      console.warn("⚠️ [Altair] Client not available or policy number empty");
      return;
    }

    console.log("📋 [Altair] Submitting policy inquiry:", policyNumberInput);

    try {
      const result = await policyInquiry(policyNumberInput);
      console.log("✅ [Altair] Policy inquiry result:", result);

      if (result.found) {
        setClaimPolicyData(result);
        // Save to session storage
        try {
          sessionStorage.setItem("saaed_claim_data", JSON.stringify({
            policyNumber: result.policyNumber,
            claimRequestNum: result.claimRequestNum,
          }));
        } catch (e) {
          console.error("❌ [Altair] Failed to save claim data:", e);
        }

        setFlowStep("idle");
        setPolicyNumberInput("");

        // Notify Ameen
        client.send(
          [{ text: `[system_ui] user_submitted_policy_inquiry policy_found=true policyNumber=${result.policyNumber} claimRequestNum=${result.claimRequestNum}` }],
          false
        );
        
        // Send next instruction
        setTimeout(() => {
          client.send(
            [
              {
                text: "[SYSTEM INSTRUCTION] Policy found successfully. Now call show_claim_survey_options to display survey method selection.",
              },
            ],
            true
          );
        }, 300);
      } else {
        client.send(
          [{ text: "[system_ui] user_submitted_policy_inquiry policy_found=false" }],
          false
        );
      }
    } catch (error: any) {
      console.error("❌ [Altair] Policy inquiry error:", error);
      client.send(
        [{ text: `[system_ui] user_submitted_policy_inquiry error=${error.message}` }],
        false
      );
    }
  }, [client, policyNumberInput, setFlowStep]);

  const handleSurveyOptionClick = useCallback((option: "live" | "upload") => {
    if (!client) return;
    
    console.log(`📋 [Altair] User selected survey option: ${option}`);
    setFlowStep("idle");
    
    if (option === "live") {
      client.send([{ text: "[system_ui] user_selected_live_survey" }], false);
      
      // Send next instruction for live survey
      setTimeout(() => {
        client.send(
          [
            {
              text: "[SYSTEM INSTRUCTION] User selected live survey. Now call start_camera_stream to begin vehicle inspection.",
            },
          ],
          true
        );
      }, 300);
    } else {
      client.send([{ text: "[system_ui] user_selected_upload_images" }], false);
      
      // Send next instruction for upload images
      setTimeout(() => {
        client.send(
          [
            {
              text: "[SYSTEM INSTRUCTION] User selected upload images. Now call request_claim_vehicle_images to request 4 vehicle photos.",
            },
          ],
          true
        );
      }, 300);
    }
  }, [client, setFlowStep]);

  const handlePolicySurveyOptionClick = useCallback((option: "live" | "upload") => {
    if (!client) return;
    
    console.log(`📋 [Altair] User selected policy survey option: ${option}`);
    setFlowStep("idle");
    
    if (option === "live") {
      client.send([{ text: "[system_ui] user_selected_policy_live_survey" }], false);
      
      // Send next instruction for live survey
      setTimeout(() => {
        client.send(
          [
            {
              text: "[SYSTEM INSTRUCTION] User selected live survey. Now call start_camera_stream to begin vehicle inspection.",
            },
          ],
          true
        );
      }, 300);
    } else {
      client.send([{ text: "[system_ui] user_selected_policy_upload_images" }], false);
      
      // Send next instruction for upload images
      setTimeout(() => {
        client.send(
          [
            {
              text: "[SYSTEM INSTRUCTION] User selected upload images. Now call request_policy_vehicle_images to request 4 vehicle photos.",
            },
          ],
          true
        );
      }, 300);
    }
  }, [client, setFlowStep]);

  const handlePolicyVehicleImagesUploaded = useCallback(async (files: File[]) => {
    if (!client) return;
    
    console.log(`📋 [Altair] User uploaded ${files.length} policy vehicle images`);
    
    const sides: VehicleSide[] = ["Front", "Right", "Back", "Left"];
    const newImages: VehicleSurveyImage[] = [];
    
    for (let i = 0; i < Math.min(files.length, 4); i++) {
      const file = files[i];
      const reader = new FileReader();
      const imageData = await new Promise<string>((resolve) => {
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });
      
      newImages.push({
        side: sides[i],
        data: imageData,
        capturedAt: new Date().toISOString(),
      });
    }
    
    setSurveyImages(newImages);
    
    if (newImages.length >= 4) {
      setFlowStep("idle");
      
      // Send images to Gemini for analysis
      const imageDataUrls = newImages.map(img => img.data);
      client.sendImagesForAnalysis(imageDataUrls, "image/jpeg");
      /*
      // Send a prompt asking Gemini to analyze the images and provide damage description
      client.send(
        [
          { 
            text: "من فضلك حلّل الصور الأربع للسيارة التي تم رفعها (الجهة الأمامية، اليمنى، الخلفية، اليسرى) واكتب وصفاً تفصيلياً لحالة السيارة والأضرار الظاهرة. اكتب الوصف بالعربية بشكل واضح ومفصل." 
          }
        ],
        false // Don't complete turn yet, wait for Gemini's analysis
      );
      
      // Notify Ameen that images were uploaded (Gemini will analyze and provide description)
      client.send(
        [{ text: "[system_ui] user_uploaded_policy_vehicle_images images_count=4" }],
        false
      );
      */
      // Send next instruction to analyze the images
      setTimeout(() => {
        client.send(
          [
            {
              text: "[SYSTEM INSTRUCTION] Vehicle images uploaded successfully. Say 'ثانية واحدة هحلل صور العربيه' then call analyze_policy_vehicle_images to analyze the vehicle condition.",
            },
          ],
          true
        );
      }, 300);
    }
  }, [client, setFlowStep]);

  const handleClaimVehicleImagesUploaded = useCallback(async (files: File[]) => {
    if (!client) return;
    
    console.log(`📋 [Altair] User uploaded ${files.length} claim vehicle images`);
    
    const sides: VehicleSide[] = ["Front", "Right", "Back", "Left"];
    const newImages: VehicleSurveyImage[] = [];
    
    for (let i = 0; i < Math.min(files.length, 4); i++) {
      const file = files[i];
      const reader = new FileReader();
      const imageData = await new Promise<string>((resolve) => {
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });
      
      newImages.push({
        side: sides[i],
        data: imageData,
        capturedAt: new Date().toISOString(),
      });
    }
    
    console.log("📸 [Altair] Setting claim survey images:", {
      count: newImages.length,
      sides: newImages.map(img => img.side),
      hasData: newImages.every(img => !!img.data && img.data.length > 0)
    });
    
    setClaimSurveyImages(newImages);
    
    // Also save to session storage for persistence
    try {
      const existingData = sessionStorage.getItem("saaed_user_data");
      const userData = existingData ? JSON.parse(existingData) : {};
      userData.claimSurveyImages = newImages;
      sessionStorage.setItem("saaed_user_data", JSON.stringify(userData));
      console.log("✅ [Altair] Claim survey images saved to session storage");
    } catch (error) {
      console.error("❌ [Altair] Failed to save claim survey images to session storage:", error);
    }
    
    if (newImages.length >= 4) {
      setFlowStep("idle");
      /*
      // Send images to Gemini for analysis
      const imageDataUrls = newImages.map(img => img.data);
      client.sendImagesForAnalysis(imageDataUrls, "image/jpeg");
      
      // Send a prompt asking Gemini to analyze the images and provide damage description
      client.send(
        [
          { 
            text: "من فضلك حلّل الصور الأربع للسيارة التي تم رفعها (الجهة الأمامية، اليمنى، الخلفية، اليسرى) واكتب وصفاً تفصيلياً لحالة السيارة والأضرار الظاهرة. اكتب الوصف بالعربية بشكل واضح ومفصل." 
          }
        ],
        false // Don't complete turn yet, wait for Gemini's analysis
      );
      
      // Notify Ameen that images were uploaded (Gemini will analyze and provide description)
      client.send(
        [{ text: "[system_ui] user_uploaded_claim_vehicle_images images_count=4" }],
        false
      );
      */
      // Send next instruction to analyze the images
      setTimeout(() => {
        client.send(
          [
            {
              text: "[SYSTEM INSTRUCTION] Vehicle images uploaded successfully. Say 'ثانية واحدة هحلل صور العربيه' then call analyze_claim_vehicle_images to analyze the vehicle condition.",
            },
          ],
          true
        );
      }, 300);
      
    }
  }, [client, setFlowStep]);

  const handleWorkshopOptionClick = useCallback((option: "contracted" | "non_contracted") => {
    if (!client) return;
    
    console.log(`📋 [Altair] User selected workshop option: ${option}`);
    setFlowStep("idle");
    
    if (option === "contracted") {
      client.send([{ text: "[system_ui] user_selected_contracted_workshop" }], false);
      
      // Send next instruction for contracted workshop
      setTimeout(() => {
        client.send(
          [
            {
              text: "[SYSTEM INSTRUCTION] User selected contracted workshop. Now call show_contracted_workshops_list to display available workshops.",
            },
          ],
          true
        );
      }, 300);
    } else {
      client.send([{ text: "[system_ui] user_selected_non_contracted_workshop" }], false);
      
      // Send next instruction for non-contracted workshop
      setTimeout(() => {
        client.send(
          [
            {
              text: "[SYSTEM INSTRUCTION] User selected non-contracted workshop. Now call show_non_contracted_workshop_form to collect workshop details.",
            },
          ],
          true
        );
      }, 300);
    }
  }, [client, setFlowStep]);

  const handleContractedWorkshopSelect = useCallback(async (workshop: ContractedWorkshop) => {
    if (!client) return;
    
    console.log(`📋 [Altair] User selected contracted workshop:`, workshop);
    
    try {
      const claimData = JSON.parse(sessionStorage.getItem("saaed_claim_data") || "{}");
      await chooseContractedWorkshop(
        workshop.workshopId,
        claimData.policyNumber || "",
        claimData.claimRequestNum || ""
      );
      
      setFlowStep("idle");
      client.send(
        [{ text: `[system_ui] user_chose_contracted_workshop workshopId=${workshop.workshopId} workshopName=${workshop.workshopName}` }],
        false
      );
      
      // Send next instruction
      setTimeout(() => {
        client.send(
          [
            {
              text: "[SYSTEM INSTRUCTION] Workshop selected successfully. Now call show_claim_number to display the claim number to the user.",
            },
          ],
          true
        );
      }, 300);
    } catch (error: any) {
      console.error("❌ [Altair] Error selecting workshop:", error);
      client.send(
        [{ text: `[system_ui] user_chose_contracted_workshop error=${error.message}` }],
        false
      );
    }
  }, [client, setFlowStep]);

  const handleNonContractedFormSubmit = useCallback(async () => {
    if (!client) return;
    
    if (!nonContractedForm.workshopName.trim()) {
      alert("برجاء إدخال اسم مركز الخدمة");
      return;
    }
    
    console.log(`📋 [Altair] Submitting non-contracted workshop:`, nonContractedForm);
    
    try {
      const claimData = JSON.parse(sessionStorage.getItem("saaed_claim_data") || "{}");
      await chooseNonContractedWorkshop(
        nonContractedForm,
        claimData.policyNumber || "",
        claimData.claimRequestNum || ""
      );
      
      setFlowStep("idle");
      setNonContractedForm({
        city: "",
        area: "",
        workshopAddress: "",
        workshopName: "",
        contactPersonName: "",
        mobile: "",
        phoneNumber: "",
        hotline: "",
      });
      
      client.send(
        [{ text: `[system_ui] user_submitted_non_contracted_workshop workshopName=${nonContractedForm.workshopName}` }],
        false
      );
      
      // Send next instruction
      setTimeout(() => {
        client.send(
          [
            {
              text: "[SYSTEM INSTRUCTION] Workshop details submitted successfully. Now call show_claim_number to display the claim number to the user.",
            },
          ],
          true
        );
      }, 300);
    } catch (error: any) {
      console.error("❌ [Altair] Error submitting workshop:", error);
      client.send(
        [{ text: `[system_ui] user_submitted_non_contracted_workshop error=${error.message}` }],
        false
      );
    }
  }, [client, nonContractedForm, setFlowStep]);

  const handleClaimNumberAcknowledge = useCallback(() => {
    if (!client) return;
    
    console.log(`📋 [Altair] User acknowledged claim number`);
    
    setFlowStep("idle");
    
    client.send(
      [{ text: `[system_ui] user_acknowledged_claim_number` }],
      false
    );
    
    // This is the end of claim flow, no next instruction needed
    console.log("✅ [Altair] Claim flow completed");
  }, [client, setFlowStep]);

  // ─────────────────────────────────────────────
  // 7) RENDER UI
  // ─────────────────────────────────────────────
  return (
    <div>
      {/* TEST MODE - Optional debug panel */}
      {TEST_MODE && (
        <div
          style={{
            position: "fixed",
            top: "10px",
            right: "10px",
            zIndex: 9999,
            backgroundColor: "#ff9800",
            padding: "15px",
            borderRadius: "8px",
            boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
          }}
        >
          <h4 style={{ margin: "0 0 10px 0", color: "white" }}>🧪 TEST MODE</h4>
          <div style={{ color: "white", fontSize: "12px" }}>
            <div>flowStep: {flowStep}</div>
            <div>companies: {companies.length}</div>
            <div>selectedPackageCode: {selectedPackageCode || "-"}</div>
          </div>
        </div>
      )}

      {/* Altair Chart Display (if used) */}
      <div className="vega-embed" ref={embedRef} />

      {/* Package selection UI */}
      {flowStep === "package_selection" && companies.length > 0 && (
        <div
          style={{
            marginTop: "20px",
            marginBottom: "40px",
            padding: "16px",
            backgroundColor: "#f5f7ff",
            borderRadius: "8px",
            border: "1px solid #c3d0ff",
          }}
        >
          <h3 style={{ marginBottom: "12px" }}>اختر الباقة المناسبة</h3>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            {companies.map((company, companyIndex) => (
              <div
                key={`company-${companyIndex}`}
                style={{
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  padding: "12px",
                  backgroundColor: "white",
                }}
              >
                <h4
                  style={{
                    marginBottom: "12px",
                    fontSize: "16px",
                    fontWeight: "bold",
                    color: "#0066ff",
                    textAlign: "right",
                    direction: "rtl",
                  }}
                >
                  {company.companyNameAr} ({company.companyNameEn})
                </h4>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {company.companyPackages.map((pkg, pkgIndex) => (
                    <button
                      key={pkg.packageCode ? `pkg-${pkg.packageCode}` : `pkg-${companyIndex}-${pkgIndex}`}
                      onClick={() => handleSelectPackage(pkg)}
                      style={{
                        padding: "10px 14px",
                        borderRadius: "6px",
                        border: selectedPackageCode === pkg.packageCode
                          ? "2px solid #0066ff"
                          : "1px solid #0066ff",
                        backgroundColor: selectedPackageCode === pkg.packageCode
                          ? "#e6f0ff"
                          : "white",
                        textAlign: "right",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: "14px",
                        direction: "rtl",
                      }}
                    >
                      <span>
                        {pkg.packageTypeAr} ({pkg.packageTypeEn})
                      </span>
                      <span style={{ fontWeight: "bold" }}>
                        {pkg.totalpremium !== undefined &&
                        pkg.totalpremium !== null
                          ? pkg.totalpremium.toLocaleString("ar-EG")
                          : "-" }{" "}
                        جنيه
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {selectedPackageCode && (
            <div style={{ marginTop: "16px", textAlign: "center" }}>
              <button
                onClick={() => {
                  // Find the selected package
                  let selectedPkg: PolicyPackage | null = null;
                  for (const company of companies) {
                    const pkg = company.companyPackages.find(p => p.packageCode === selectedPackageCode);
                    if (pkg) {
                      selectedPkg = pkg;
                      break;
                    }
                  }
                  if (selectedPkg && client) {
                    // Save package price to session storage
                    try {
                      sessionStorage.setItem("selected_package_price", selectedPkg.totalpremium.toString());
                      console.log(`✅ [Altair] Saved selected package price ${selectedPkg.totalpremium} to session storage`);
                    } catch (error) {
                      console.error("❌ [Altair] Failed to save package price to session storage:", error);
                    }
                    
                    // Send confirm event
                    client.send(
                      [
                        {
                          text: `[system_ui] user_clicked_package_confirm: ${selectedPkg.packageCode}|${selectedPkg.packageTypeAr}|${selectedPkg.packageTypeEn}|${selectedPkg.totalpremium}`,
                        },
                      ],
                      false
                    );
                    
                    // Send next instruction
                    setTimeout(() => {
                      client.send(
                        [
                          {
                            text: "[SYSTEM INSTRUCTION] Package confirmed successfully. Now call request_national_id_front to start collecting identity documents.",
                          },
                        ],
                        true
                      );
                    }, 300);
                    
                    setFlowStep("idle");
                  }
                }}
                style={{
                  padding: "12px 24px",
                  borderRadius: "6px",
                  border: "none",
                  backgroundColor: "#0066ff",
                  color: "white",
                  fontSize: "16px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  minWidth: "150px",
                }}
              >
                تأكيد الباقة
              </button>
            </div>
          )}
        </div>
      )}

      {/* National ID upload - front */}
      {flowStep === "national_id_front" && (
        <div style={{ marginTop: "20px", marginBottom: "40px" }}>
          <h3 style={{ marginBottom: "10px" }}>
            رفع صورة وش بطاقة الرقم القومي
          </h3>
          <ImageUpload onImagesUploaded={handleImagesUploaded} maxImages={1} />
        </div>
      )}

      {/* National ID upload - back */}
      {flowStep === "national_id_back" && (
        <div style={{ marginTop: "20px", marginBottom: "40px" }}>
          <h3 style={{ marginBottom: "10px" }}>
            رفع صورة ظهر بطاقة الرقم القومي
          </h3>
          <ImageUpload onImagesUploaded={handleImagesUploaded} maxImages={1} />
        </div>
      )}

      {/* Driving License upload */}
      {flowStep === "driving_license" && (
        <div style={{ marginTop: "20px", marginBottom: "40px" }}>
          <h3 style={{ marginBottom: "10px" }}>
            رفع صورة رخصة القيادة
          </h3>
          <ImageUpload onImagesUploaded={handleImagesUploaded} maxImages={1} />
        </div>
      )}

      {/* Vehicle License upload - front */}
      {flowStep === "vehicle_license_front" && (
        <div style={{ marginTop: "20px", marginBottom: "40px" }}>
          <h3 style={{ marginBottom: "10px" }}>
            رفع صورة وش رخصة السيارة
          </h3>
          <ImageUpload onImagesUploaded={handleImagesUploaded} maxImages={1} />
        </div>
      )}

      {/* Vehicle License upload - back */}
      {flowStep === "vehicle_license_back" && (
        <div style={{ marginTop: "20px", marginBottom: "40px" }}>
          <h3 style={{ marginBottom: "10px" }}>
            رفع صورة ظهر رخصة السيارة
          </h3>
          <ImageUpload onImagesUploaded={handleImagesUploaded} maxImages={1} />
        </div>
      )}

      {/* Vehicle Survey Status - Car Image with Checkboxes */}
      {(flowStep === "vehicle_survey" || flowStep === "claim_vehicle_survey") && (() => {
        // Determine which images array to use based on flow
        const isClaimFlow = flowStep === "claim_vehicle_survey" || claimSurveyImages.length > 0;
        const currentImages = isClaimFlow ? claimSurveyImages : surveyImages;
        const totalImages = currentImages.length;
        
        // Check which sides are captured
        const frontCaptured = currentImages.some(img => img.side === "Front");
        const rightCaptured = currentImages.some(img => img.side === "Right");
        const backCaptured = currentImages.some(img => img.side === "Back");
        const leftCaptured = currentImages.some(img => img.side === "Left");
        
        return (
          <div
            style={{
              position: "fixed",
              top: "16px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "calc(100% - 32px)",
              maxWidth: "400px",
              padding: "16px",
              backgroundColor: "rgba(0, 0, 0, 0.85)",
              borderRadius: "16px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
              backdropFilter: "blur(10px)",
              direction: "rtl",
              zIndex: 100,
            }}
          >
            {/* Live indicator + title */}
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              gap: "8px",
              marginBottom: "12px",
            }}>
              <div style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "#4caf50",
                animation: "surveyPulse 1.5s ease-in-out infinite",
                boxShadow: "0 0 6px rgba(76, 175, 80, 0.8)",
              }} />
              <span style={{ 
                fontSize: "14px",
                fontWeight: "500",
                color: "#fff",
              }}>
                معاينة المركبة
              </span>
            </div>

            {/* Car Image with Checkboxes */}
            <div style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
            }}>
              {/* Car Image */}
              <div style={{
                position: "relative",
                width: "200px",
                height: "120px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
                <img 
                  src="/CarImage.png" 
                  alt="Car" 
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
                
                {/* Checkboxes positioned around the car */}
                {/* Front Checkbox - positioned on the right side of the car image */}
                <div style={{
                  position: "absolute",
                  right: "-8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "4px",
                }}>
                  <input
                    type="checkbox"
                    checked={frontCaptured}
                    readOnly
                    disabled={false}
                    style={{
                      width: "24px",
                      height: "24px",
                      cursor: "default",
                      accentColor: "#4caf50",
                    }}
                  />
                  <div style={{
                    fontSize: "11px",
                    color: "#fff",
                    textAlign: "center",
                    fontWeight: "500",
                    whiteSpace: "nowrap",
                  }}>أمامي</div>
                </div>

                {/* Back Checkbox - positioned on the left side of the car image */}
                <div style={{
                  position: "absolute",
                  left: "-8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "4px",
                }}>
                  <input
                    type="checkbox"
                    checked={backCaptured}
                    readOnly
                    disabled={false}
                    style={{
                      width: "24px",
                      height: "24px",
                      cursor: "default",
                      accentColor: "#4caf50",
                    }}
                  />
                  <div style={{
                    fontSize: "11px",
                    color: "#fff",
                    textAlign: "center",
                    fontWeight: "500",
                    whiteSpace: "nowrap",
                  }}>خلفي</div>
                </div>

                {/* Right Checkbox - positioned at the bottom of the car image */}
                <div style={{
                  position: "absolute",
                  bottom: "-8px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "4px",
                }}>
                  <input
                    type="checkbox"
                    checked={rightCaptured}
                    readOnly
                    disabled={false}
                    style={{
                      width: "24px",
                      height: "24px",
                      cursor: "default",
                      accentColor: "#4caf50",
                    }}
                  />
                  <div style={{
                    fontSize: "11px",
                    color: "#fff",
                    textAlign: "center",
                    fontWeight: "500",
                    whiteSpace: "nowrap",
                  }}>يمين</div>
                </div>

                {/* Left Checkbox - positioned at the top of the car image */}
                <div style={{
                  position: "absolute",
                  top: "-8px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "4px",
                }}>
                  <input
                    type="checkbox"
                    checked={leftCaptured}
                    readOnly
                    disabled={false}
                    style={{
                      width: "24px",
                      height: "24px",
                      cursor: "default",
                      accentColor: "#4caf50",
                    }}
                  />
                  <div style={{
                    fontSize: "11px",
                    color: "#fff",
                    textAlign: "center",
                    fontWeight: "500",
                    whiteSpace: "nowrap",
                  }}>يسار</div>
                </div>
              </div>

              {/* Counter */}
              <div style={{
                fontSize: "12px",
                color: "rgba(255, 255, 255, 0.9)",
                fontWeight: "500",
              }}>
                {totalImages}/4 صور
              </div>
            </div>
          </div>
        );
      })()}

      {/* CSS animations for survey pulse */}
      {(flowStep === "vehicle_survey" || flowStep === "claim_vehicle_survey") && (
        <style>{`
          @keyframes surveyPulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.2); }
          }
        `}</style>
      )}

      {/* Policy: Survey Options (Live / Upload) */}
      {flowStep === "policy_survey_options" && (
        <div
          style={{
            marginTop: "clamp(10px, 2vw, 20px)",
            marginBottom: "clamp(20px, 4vw, 40px)",
            padding: "clamp(12px, 3vw, 20px)",
            backgroundColor: "#e3f2fd",
            borderRadius: "8px",
            border: "1px solid #2196f3",
            direction: "rtl",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          <h3 style={{ marginBottom: "clamp(12px, 2vw, 16px)", color: "#1565c0", fontSize: "clamp(16px, 4vw, 20px)" }}>اختر طريقة المعاينة</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "clamp(10px, 2vw, 12px)" }}>
            <button
              onClick={() => handlePolicySurveyOptionClick("live")}
              style={{
                padding: "clamp(12px, 3vw, 16px)",
                borderRadius: "8px",
                border: "2px solid #2196f3",
                backgroundColor: "white",
                cursor: "pointer",
                fontSize: "clamp(14px, 3.5vw, 16px)",
                display: "flex",
                alignItems: "center",
                gap: "clamp(8px, 2vw, 12px)",
                minHeight: "60px", // Touch-friendly
              }}
            >
              <span style={{ fontSize: "clamp(20px, 5vw, 24px)" }}>📹</span>
              <div style={{ textAlign: "right", flex: 1 }}>
                <div style={{ fontWeight: "bold", fontSize: "clamp(14px, 3.5vw, 16px)" }}>معاينة لايف</div>
                <div style={{ fontSize: "clamp(11px, 2.5vw, 12px)", color: "#666" }}>فتح الكاميرا والتصوير مباشرة</div>
              </div>
            </button>
            <button
              onClick={() => handlePolicySurveyOptionClick("upload")}
              style={{
                padding: "clamp(12px, 3vw, 16px)",
                borderRadius: "8px",
                border: "2px solid #2196f3",
                backgroundColor: "white",
                cursor: "pointer",
                fontSize: "clamp(14px, 3.5vw, 16px)",
                display: "flex",
                alignItems: "center",
                gap: "clamp(8px, 2vw, 12px)",
                minHeight: "60px", // Touch-friendly
              }}
            >
              <span style={{ fontSize: "clamp(20px, 5vw, 24px)" }}>📤</span>
              <div style={{ textAlign: "right", flex: 1 }}>
                <div style={{ fontWeight: "bold", fontSize: "clamp(14px, 3.5vw, 16px)" }}>رفع 4 صور</div>
                <div style={{ fontSize: "clamp(11px, 2.5vw, 12px)", color: "#666" }}>رفع 4 صور للسيارة من الجهات الأربعة</div>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Policy: Vehicle Images Upload (4 images) */}
      {flowStep === "policy_vehicle_upload" && (
        <div
          style={{
            marginTop: "clamp(10px, 2vw, 20px)",
            marginBottom: "clamp(20px, 4vw, 40px)",
            padding: "clamp(12px, 3vw, 20px)",
            backgroundColor: "#e8f5e9",
            borderRadius: "8px",
            border: "1px solid #4caf50",
            direction: "rtl",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          <h3 style={{ marginBottom: "clamp(8px, 1.5vw, 12px)", color: "#2e7d32", fontSize: "clamp(16px, 4vw, 20px)" }}>صور المركبة</h3>
          <p style={{ marginBottom: "clamp(12px, 2vw, 16px)", fontSize: "clamp(12px, 3vw, 14px)", color: "#666" }}>
            ارفع 4 صور للسيارة: الأمامية، اليمنى، الخلفية، اليسرى
          </p>
          <ImageUpload 
            onImagesUploaded={handlePolicyVehicleImagesUploaded} 
            maxImages={4}
            title="صور المركبة"
          />
          {surveyImages.length > 0 && (
            <div style={{ marginTop: "clamp(10px, 2vw, 12px)", fontSize: "clamp(12px, 3vw, 14px)", color: "#4caf50" }}>
              تم رفع {surveyImages.length} من 4 صور
            </div>
          )}
        </div>
      )}

      {/* Policy confirmation screen */}
      {flowStep === "policy_confirmation" && selectedPackage && (
        <div
          style={{
            marginTop: "20px",
            marginBottom: "40px",
            padding: "16px",
            backgroundColor: "#f0fdf4",
            borderRadius: "8px",
            border: "1px solid #4caf50",
            direction: "rtl",
          }}
        >
          <h3 style={{ marginBottom: "12px" }}>تأكيد الباقة قبل إرسال الطلب</h3>
          <div
            style={{
              marginBottom: "16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              fontSize: "14px",
            }}
          >
            <span>
              {selectedPackage.packageTypeAr} ({selectedPackage.packageTypeEn})
            </span>
            <span style={{ fontWeight: "bold" }}>
              {selectedPackage.totalpremium.toLocaleString("ar-EG")} جنيه
            </span>
          </div>
          <button
            onClick={handleConfirmPolicyClick}
            style={{
              padding: "10px 16px",
              borderRadius: "6px",
              border: "none",
              backgroundColor: "#4caf50",
              color: "white",
              cursor: "pointer",
              fontSize: "14px",
              width: "100%",
            }}
          >
            تأكيد الباقة وإرسال لينك الدفع
          </button>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* CLAIM REQUEST UI COMPONENTS */}
      {/* ═══════════════════════════════════════════════════════════ */}

      {/* Claim: Policy Inquiry Popup */}
      {flowStep === "claim_policy_inquiry" && (
        <div
          style={{
            marginTop: "clamp(10px, 2vw, 20px)",
            marginBottom: "clamp(20px, 4vw, 40px)",
            padding: "clamp(12px, 3vw, 20px)",
            backgroundColor: "#fff8e1",
            borderRadius: "8px",
            border: "1px solid #ffb300",
            direction: "rtl",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          <h3 style={{ marginBottom: "clamp(12px, 2vw, 16px)", color: "#f57c00", fontSize: "clamp(16px, 4vw, 20px)" }}>رقم الوثيقة</h3>
          <input
            type="text"
            value={policyNumberInput}
            onChange={(e) => setPolicyNumberInput(e.target.value)}
            placeholder="أدخل رقم الوثيقة"
            style={{
              width: "100%",
              padding: "clamp(10px, 2.5vw, 12px)",
              fontSize: "clamp(14px, 3.5vw, 16px)",
              borderRadius: "6px",
              border: "1px solid #ffb300",
              marginBottom: "clamp(12px, 2vw, 16px)",
              textAlign: "right",
              boxSizing: "border-box",
            }}
          />
          <button
            onClick={handlePolicyInquirySubmit}
            disabled={!policyNumberInput.trim()}
            style={{
              padding: "clamp(12px, 2.5vw, 14px) clamp(16px, 3vw, 20px)",
              borderRadius: "6px",
              border: "none",
              backgroundColor: policyNumberInput.trim() ? "#ff9800" : "#ccc",
              color: "white",
              cursor: policyNumberInput.trim() ? "pointer" : "not-allowed",
              fontSize: "clamp(14px, 3.5vw, 16px)",
              width: "100%",
              fontWeight: "bold",
              minHeight: "44px", // Touch-friendly
            }}
          >
            بحث عن الوثيقة
          </button>
        </div>
      )}

      {/* Claim: Survey Options (Live / Upload) */}
      {flowStep === "claim_survey_options" && (
        <div
          style={{
            marginTop: "clamp(10px, 2vw, 20px)",
            marginBottom: "clamp(20px, 4vw, 40px)",
            padding: "clamp(12px, 3vw, 20px)",
            backgroundColor: "#e3f2fd",
            borderRadius: "8px",
            border: "1px solid #2196f3",
            direction: "rtl",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          <h3 style={{ marginBottom: "clamp(12px, 2vw, 16px)", color: "#1565c0", fontSize: "clamp(16px, 4vw, 20px)" }}>اختر طريقة المعاينة</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "clamp(10px, 2vw, 12px)" }}>
            <button
              onClick={() => handleSurveyOptionClick("live")}
              style={{
                padding: "clamp(12px, 3vw, 16px)",
                borderRadius: "8px",
                border: "2px solid #2196f3",
                backgroundColor: "white",
                cursor: "pointer",
                fontSize: "clamp(14px, 3.5vw, 16px)",
                display: "flex",
                alignItems: "center",
                gap: "clamp(8px, 2vw, 12px)",
                minHeight: "60px", // Touch-friendly
              }}
            >
              <span style={{ fontSize: "clamp(20px, 5vw, 24px)" }}>📹</span>
              <div style={{ textAlign: "right", flex: 1 }}>
                <div style={{ fontWeight: "bold", fontSize: "clamp(14px, 3.5vw, 16px)" }}>معاينة لايف</div>
                <div style={{ fontSize: "clamp(11px, 2.5vw, 12px)", color: "#666" }}>فتح الكاميرا والتصوير مباشرة</div>
              </div>
            </button>
            <button
              onClick={() => handleSurveyOptionClick("upload")}
              style={{
                padding: "clamp(12px, 3vw, 16px)",
                borderRadius: "8px",
                border: "2px solid #2196f3",
                backgroundColor: "white",
                cursor: "pointer",
                fontSize: "clamp(14px, 3.5vw, 16px)",
                display: "flex",
                alignItems: "center",
                gap: "clamp(8px, 2vw, 12px)",
                minHeight: "60px", // Touch-friendly
              }}
            >
              <span style={{ fontSize: "clamp(20px, 5vw, 24px)" }}>📤</span>
              <div style={{ textAlign: "right", flex: 1 }}>
                <div style={{ fontWeight: "bold", fontSize: "clamp(14px, 3.5vw, 16px)" }}>رفع صور</div>
                <div style={{ fontSize: "clamp(11px, 2.5vw, 12px)", color: "#666" }}>رفع 4 صور للسيارة من الجهات الأربعة</div>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Claim: Vehicle Images Upload (4 images) */}
      {flowStep === "claim_vehicle_upload" && (
        <div
          style={{
            marginTop: "clamp(10px, 2vw, 20px)",
            marginBottom: "clamp(20px, 4vw, 40px)",
            padding: "clamp(12px, 3vw, 20px)",
            backgroundColor: "#e8f5e9",
            borderRadius: "8px",
            border: "1px solid #4caf50",
            direction: "rtl",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          <h3 style={{ marginBottom: "clamp(8px, 1.5vw, 12px)", color: "#2e7d32", fontSize: "clamp(16px, 4vw, 20px)" }}>صور المركبة</h3>
          <p style={{ marginBottom: "clamp(12px, 2vw, 16px)", fontSize: "clamp(12px, 3vw, 14px)", color: "#666" }}>
            ارفع 4 صور للسيارة: الأمامية، اليمنى، الخلفية، اليسرى
          </p>
          <ImageUpload 
            onImagesUploaded={handleClaimVehicleImagesUploaded} 
            maxImages={4}
            title="صور المركبة"
          />
          {claimSurveyImages.length > 0 && (
            <div style={{ marginTop: "clamp(10px, 2vw, 12px)", fontSize: "clamp(12px, 3vw, 14px)", color: "#4caf50" }}>
              تم رفع {claimSurveyImages.length} من 4 صور
            </div>
          )}
        </div>
      )}

      {/* Claim: Workshop Options (Contracted / Non-Contracted) */}
      {flowStep === "claim_workshop_options" && (
        <div
          style={{
            marginTop: "clamp(10px, 2vw, 20px)",
            marginBottom: "clamp(20px, 4vw, 40px)",
            padding: "clamp(12px, 3vw, 20px)",
            backgroundColor: "#fce4ec",
            borderRadius: "8px",
            border: "1px solid #e91e63",
            direction: "rtl",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          <h3 style={{ marginBottom: "clamp(12px, 2vw, 16px)", color: "#c2185b", fontSize: "clamp(16px, 4vw, 20px)" }}>اختر مركز الإصلاح</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "clamp(10px, 2vw, 12px)" }}>
            <button
              onClick={() => handleWorkshopOptionClick("contracted")}
              style={{
                padding: "clamp(12px, 3vw, 16px)",
                borderRadius: "8px",
                border: "2px solid #e91e63",
                backgroundColor: "white",
                cursor: "pointer",
                fontSize: "clamp(14px, 3.5vw, 16px)",
                display: "flex",
                alignItems: "center",
                gap: "clamp(8px, 2vw, 12px)",
                minHeight: "60px", // Touch-friendly
              }}
            >
              <span style={{ fontSize: "clamp(20px, 5vw, 24px)" }}>🏢</span>
              <div style={{ textAlign: "right", flex: 1 }}>
                <div style={{ fontWeight: "bold", fontSize: "clamp(14px, 3.5vw, 16px)" }}>مركز خدمة متعاقد</div>
                <div style={{ fontSize: "clamp(11px, 2.5vw, 12px)", color: "#666" }}>اختر من قائمة المراكز المتعاقدة</div>
              </div>
            </button>
            <button
              onClick={() => handleWorkshopOptionClick("non_contracted")}
              style={{
                padding: "clamp(12px, 3vw, 16px)",
                borderRadius: "8px",
                border: "2px solid #e91e63",
                backgroundColor: "white",
                cursor: "pointer",
                fontSize: "clamp(14px, 3.5vw, 16px)",
                display: "flex",
                alignItems: "center",
                gap: "clamp(8px, 2vw, 12px)",
                minHeight: "60px", // Touch-friendly
              }}
            >
              <span style={{ fontSize: "clamp(20px, 5vw, 24px)" }}>🔧</span>
              <div style={{ textAlign: "right", flex: 1 }}>
                <div style={{ fontWeight: "bold", fontSize: "clamp(14px, 3.5vw, 16px)" }}>مركز خدمة غير متعاقد</div>
                <div style={{ fontSize: "clamp(11px, 2.5vw, 12px)", color: "#666" }}>أدخل بيانات مركز خدمة آخر</div>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Claim: Contracted Workshops List */}
      {flowStep === "claim_contracted_workshops" && (
  <>
    <style>{`
      .workshop-card {
        width: 100%;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        gap: 6px;
        position: relative;
        text-align: right;
      }

      .nearest-badge {
        position: absolute;
        top: 6px;
        right: 6px;
        background: #4caf50;
        color: #fff;
        padding: 3px 8px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: bold;
        white-space: nowrap;
      }

      .workshop-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 8px;
      }

      .workshop-name {
        font-weight: bold;
        font-size: 15px;
        line-height: 1.4;
        color: #000;
        flex: 1;
        word-break: break-word;
      }

      .workshop-distance {
        font-size: 11px;
        color: #2e7d32;
        font-weight: bold;
        white-space: nowrap;
        flex-shrink: 0;
      }

      .workshop-address {
        font-size: 12px;
        color: #555;
        line-height: 1.5;
        word-break: break-word;
      }

      .workshop-city-phone {
        font-size: 11px;
        color: #777;
        line-height: 1.4;
        word-break: break-word;
      }

      @media (max-width: 480px) {
        .workshop-name {
          font-size: 14px;
        }
        .workshop-address {
          font-size: 11.5px;
        }
      }
    `}</style>

    <div
      style={{
        marginTop: 16,
        padding: 16,
        backgroundColor: "#f3e5f5",
        borderRadius: 8,
        border: "1px solid #9c27b0",
        direction: "rtl",
      }}
    >
      <h3
        style={{
          marginBottom: 12,
          color: "#7b1fa2",
          fontSize: 17,
        }}
      >
        اختر مركز الخدمة
      </h3>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          maxHeight: 320,
          overflowY: "auto",
        }}
      >
        {contractedWorkshops.map((workshop) => (
          <button
            key={workshop.workshopId}
            onClick={() => handleContractedWorkshopSelect(workshop)}
            className="workshop-card"
            style={{
              padding: 14,
              paddingTop: workshop.isNearest ? 32 : 14,
              borderRadius: 8,
              border: workshop.isNearest
                ? "2px solid #4caf50"
                : "1px solid #9c27b0",
              backgroundColor: workshop.isNearest ? "#e8f5e9" : "#fff",
              cursor: "pointer",
            }}
          >
            {workshop.isNearest && (
              <div className="nearest-badge">الأقرب</div>
            )}

            <div className="workshop-header">
              <div className="workshop-name">
                {workshop.workshopName}
              </div>

              {workshop.distance != null && (
                <div className="workshop-distance">
                  {workshop.distance < 1000
                    ? `${Math.round(workshop.distance)} م`
                    : `${(workshop.distance / 1000).toFixed(1)} كم`}
                </div>
              )}
            </div>

            <div className="workshop-address">
              {workshop.address}
            </div>

            <div className="workshop-city-phone">
              {workshop.city}
              {workshop.phoneNumber && ` - ${workshop.phoneNumber}`}
            </div>
          </button>
        ))}
      </div>
    </div>
  </>
)}


      {/* Claim: Non-Contracted Workshop Form */}
      {flowStep === "claim_non_contracted_form" && (
        <>
          <style>{`
            @media (min-width: 600px) {
              .claim-form-row {
                flex-direction: row !important;
              }
            }
            @media (max-width: 599px) {
              .claim-form-row {
                flex-direction: column !important;
              }
              .claim-form-row input {
                width: 100% !important;
                flex: none !important;
              }
            }
          `}</style>
          <div
            style={{
              marginTop: "clamp(10px, 2vw, 20px)",
              marginBottom: "clamp(20px, 4vw, 40px)",
              padding: "clamp(12px, 3vw, 20px)",
              backgroundColor: "#fff3e0",
              borderRadius: "8px",
              border: "1px solid #ff9800",
              direction: "rtl",
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            <h3 style={{ marginBottom: "clamp(12px, 2vw, 16px)", color: "#e65100", fontSize: "clamp(16px, 4vw, 20px)" }}>بيانات مركز الإصلاح</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "clamp(10px, 2vw, 12px)" }}>
              <input
                type="text"
                placeholder="اسم مركز الخدمة *"
                value={nonContractedForm.workshopName}
                onChange={(e) => setNonContractedForm(prev => ({ ...prev, workshopName: e.target.value }))}
                style={{ 
                  padding: "clamp(10px, 2.5vw, 12px)", 
                  borderRadius: "6px", 
                  border: "1px solid #ff9800", 
                  textAlign: "right",
                  fontSize: "clamp(14px, 3.5vw, 16px)",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              />
              <div className="claim-form-row" style={{ display: "flex", gap: "clamp(8px, 2vw, 12px)", flexDirection: "column" }}>
                <input
                  type="text"
                  placeholder="المدينة"
                  value={nonContractedForm.city}
                  onChange={(e) => setNonContractedForm(prev => ({ ...prev, city: e.target.value }))}
                  style={{ 
                    flex: 1, 
                    padding: "clamp(10px, 2.5vw, 12px)", 
                    borderRadius: "6px", 
                    border: "1px solid #ff9800", 
                    textAlign: "right",
                    fontSize: "clamp(14px, 3.5vw, 16px)",
                    boxSizing: "border-box",
                    minWidth: 0,
                  }}
                />
                <input
                  type="text"
                  placeholder="المنطقة"
                  value={nonContractedForm.area}
                  onChange={(e) => setNonContractedForm(prev => ({ ...prev, area: e.target.value }))}
                  style={{ 
                    flex: 1, 
                    padding: "clamp(10px, 2.5vw, 12px)", 
                    borderRadius: "6px", 
                    border: "1px solid #ff9800", 
                    textAlign: "right",
                    fontSize: "clamp(14px, 3.5vw, 16px)",
                    boxSizing: "border-box",
                    minWidth: 0,
                  }}
                />
              </div>
              <input
                type="text"
                placeholder="العنوان"
                value={nonContractedForm.workshopAddress}
                onChange={(e) => setNonContractedForm(prev => ({ ...prev, workshopAddress: e.target.value }))}
                style={{ 
                  padding: "clamp(10px, 2.5vw, 12px)", 
                  borderRadius: "6px", 
                  border: "1px solid #ff9800", 
                  textAlign: "right",
                  fontSize: "clamp(14px, 3.5vw, 16px)",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              />
              <input
                type="text"
                placeholder="اسم المسؤول"
                value={nonContractedForm.contactPersonName}
                onChange={(e) => setNonContractedForm(prev => ({ ...prev, contactPersonName: e.target.value }))}
                style={{ 
                  padding: "clamp(10px, 2.5vw, 12px)", 
                  borderRadius: "6px", 
                  border: "1px solid #ff9800", 
                  textAlign: "right",
                  fontSize: "clamp(14px, 3.5vw, 16px)",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              />
              <div className="claim-form-row" style={{ display: "flex", gap: "clamp(8px, 2vw, 12px)", flexDirection: "column" }}>
                <input
                  type="tel"
                  placeholder="الموبايل"
                  value={nonContractedForm.mobile}
                  onChange={(e) => setNonContractedForm(prev => ({ ...prev, mobile: e.target.value }))}
                  style={{ 
                    flex: 1, 
                    padding: "clamp(10px, 2.5vw, 12px)", 
                    borderRadius: "6px", 
                    border: "1px solid #ff9800", 
                    textAlign: "right",
                    fontSize: "clamp(14px, 3.5vw, 16px)",
                    boxSizing: "border-box",
                    minWidth: 0,
                  }}
                />
                <input
                  type="tel"
                  placeholder="التليفون"
                  value={nonContractedForm.phoneNumber}
                  onChange={(e) => setNonContractedForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                  style={{ 
                    flex: 1, 
                    padding: "clamp(10px, 2.5vw, 12px)", 
                    borderRadius: "6px", 
                    border: "1px solid #ff9800", 
                    textAlign: "right",
                    fontSize: "clamp(14px, 3.5vw, 16px)",
                    boxSizing: "border-box",
                    minWidth: 0,
                  }}
                />
              </div>
              <input
                type="tel"
                placeholder="الخط الساخن"
                value={nonContractedForm.hotline}
                onChange={(e) => setNonContractedForm(prev => ({ ...prev, hotline: e.target.value }))}
                style={{ 
                  padding: "clamp(10px, 2.5vw, 12px)", 
                  borderRadius: "6px", 
                  border: "1px solid #ff9800", 
                  textAlign: "right",
                  fontSize: "clamp(14px, 3.5vw, 16px)",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              />
              <button
                onClick={handleNonContractedFormSubmit}
                style={{
                  padding: "clamp(12px, 2.5vw, 14px) clamp(16px, 3vw, 20px)",
                  borderRadius: "6px",
                  border: "none",
                  backgroundColor: "#ff9800",
                  color: "white",
                  cursor: "pointer",
                  fontSize: "clamp(14px, 3.5vw, 16px)",
                  fontWeight: "bold",
                  marginTop: "clamp(8px, 1.5vw, 12px)",
                  width: "100%",
                  minHeight: "44px", // Touch-friendly
                }}
              >
                تأكيد بيانات المركز
              </button>
            </div>
          </div>
        </>
      )}

      {/* Claim: Claim Number Display */}
      {flowStep === "claim_number_display" && (() => {
        const claimData = JSON.parse(sessionStorage.getItem("saaed_claim_data") || "{}");
        const claimNumber = claimData.claimRequestNum || "";
        
        return (
          <div
            style={{
              marginTop: "clamp(10px, 2vw, 20px)",
              marginBottom: "clamp(20px, 4vw, 40px)",
              padding: "clamp(12px, 3vw, 20px)",
              backgroundColor: "#e8f5e9",
              borderRadius: "8px",
              border: "1px solid #4caf50",
              direction: "rtl",
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            <h3 style={{ marginBottom: "clamp(12px, 2vw, 16px)", color: "#2e7d32", fontSize: "clamp(16px, 4vw, 20px)" }}>رقم المطالبة</h3>
            <div
              style={{
                padding: "clamp(12px, 2.5vw, 16px)",
                backgroundColor: "#ffffff",
                borderRadius: "6px",
                border: "2px solid #4caf50",
                marginBottom: "clamp(12px, 2vw, 16px)",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "clamp(18px, 5vw, 24px)",
                  fontWeight: "bold",
                  color: "#2e7d32",
                  letterSpacing: "clamp(1px, 0.5vw, 2px)",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {claimNumber || "غير متوفر"}
              </div>
            </div>
            <button
              onClick={handleClaimNumberAcknowledge}
              style={{
                padding: "clamp(12px, 2.5vw, 14px) clamp(16px, 3vw, 20px)",
                borderRadius: "6px",
                border: "none",
                backgroundColor: "#4caf50",
                color: "white",
                cursor: "pointer",
                fontSize: "clamp(14px, 3.5vw, 16px)",
                fontWeight: "bold",
                width: "100%",
                minHeight: "44px", // Touch-friendly
              }}
            >
              تم
            </button>
          </div>
        );
      })()}

      {/* (اختياري) Debug: عرض حالة بيانات البطاقة لو حابة تشوفيها */}
      {/* 
      <pre style={{ fontSize: 10 }}>
        {JSON.stringify(customerNationalIdData, null, 2)}
      </pre>
      */}

      {/* Extracted Data Confirmation Modal */}
      <ExtractedDataConfirmationModal
        isOpen={extractedDataModal.isOpen}
        title={extractedDataModal.title}
        data={extractedDataModal.data}
        documentType={extractedDataModal.documentType}
        onConfirm={extractedDataModal.onConfirm}
        onSaveAndConfirm={extractedDataModal.onSaveAndConfirm}
        onCancel={() => setExtractedDataModal(prev => ({ ...prev, isOpen: false }))}
      />

      {/* Policy Number Modal */}
      <PolicyNumberModal
        isOpen={policyNumberModal.isOpen}
        policyNumber={policyNumberModal.policyNumber}
        title={policyNumberModal.title}
        message={policyNumberModal.message}
        onConfirm={policyNumberModal.onConfirm}
      />
    </div>
  );
}

export const Altair = memo(AltairComponent);
